import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4361ee',
      light: '#738eef',
      dark: '#2c41b0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3f37c9',
      light: '#7a72df',
      dark: '#271f8f',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4cc9f0',
      light: '#a5e9ff',
      dark: '#0096c7',
    },
    error: {
      main: '#f72585',
      light: '#ff80b5',
      dark: '#b5006a',
    },
    warning: {
      main: '#f8961e',
      light: '#ffbd59',
      dark: '#c26401',
    },
    info: {
      main: '#4895ef',
      light: '#90c2ff',
      dark: '#0468c1',
    },
    background: {
      default: '#f6f8ff',
      paper: '#ffffff',
      light: '#f8faff',
      gradient: 'linear-gradient(145deg, #f8faff 0%, #f2f6ff 100%)',
    },
    text: {
      primary: '#2b2d42',
      secondary: '#6c757d',
      disabled: '#adb5bd',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.08)',
    '0px 3px 6px rgba(0, 0, 0, 0.06), 0px 3px 6px rgba(0, 0, 0, 0.10)',
    '0px 4px 8px rgba(0, 0, 0, 0.07), 0px 4px 8px rgba(0, 0, 0, 0.12)',
    '0px 5px 10px rgba(0, 0, 0, 0.08), 0px 5px 10px rgba(0, 0, 0, 0.14)',
    '0px 6px 12px rgba(0, 0, 0, 0.09), 0px 6px 12px rgba(0, 0, 0, 0.16)',
    '0px 7px 14px rgba(0, 0, 0, 0.10), 0px 7px 14px rgba(0, 0, 0, 0.18)',
    '0px 8px 16px rgba(0, 0, 0, 0.11), 0px 8px 16px rgba(0, 0, 0, 0.20)',
    '0px 9px 18px rgba(0, 0, 0, 0.12), 0px 9px 18px rgba(0, 0, 0, 0.22)',
    '0px 10px 20px rgba(0, 0, 0, 0.13), 0px 10px 20px rgba(0, 0, 0, 0.24)',
    '0px 11px 22px rgba(0, 0, 0, 0.14), 0px 11px 22px rgba(0, 0, 0, 0.26)',
    '0px 12px 24px rgba(0, 0, 0, 0.15), 0px 12px 24px rgba(0, 0, 0, 0.28)',
    '0px 13px 26px rgba(0, 0, 0, 0.16), 0px 13px 26px rgba(0, 0, 0, 0.30)',
    '0px 14px 28px rgba(0, 0, 0, 0.17), 0px 14px 28px rgba(0, 0, 0, 0.32)',
    '0px 15px 30px rgba(0, 0, 0, 0.18), 0px 15px 30px rgba(0, 0, 0, 0.34)',
    '0px 16px 32px rgba(0, 0, 0, 0.19), 0px 16px 32px rgba(0, 0, 0, 0.36)',
    '0px 17px 34px rgba(0, 0, 0, 0.18), 0px 16px 32px rgba(0, 0, 0, 0.20)',
    '0px 18px 36px rgba(0, 0, 0, 0.19), 0px 17px 34px rgba(0, 0, 0, 0.21)',
    '0px 19px 38px rgba(0, 0, 0, 0.20), 0px 18px 36px rgba(0, 0, 0, 0.22)',
    '0px 20px 40px rgba(0, 0, 0, 0.21), 0px 19px 38px rgba(0, 0, 0, 0.23)',
    '0px 21px 42px rgba(0, 0, 0, 0.22), 0px 20px 40px rgba(0, 0, 0, 0.24)',
    '0px 22px 44px rgba(0, 0, 0, 0.23), 0px 21px 42px rgba(0, 0, 0, 0.25)',
    '0px 23px 46px rgba(0, 0, 0, 0.24), 0px 22px 44px rgba(0, 0, 0, 0.26)',
    '0px 24px 48px rgba(0, 0, 0, 0.25), 0px 23px 46px rgba(0, 0, 0, 0.27)',
    '0px 24px 48px rgba(0, 0, 0, 0.30), 0px 24px 48px rgba(0, 0, 0, 0.32)',
  ],
  transitions: {
    create: () => 'none',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(67, 97, 238, 0.15)',
          },
          transition: 'none',
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(67, 97, 238, 0.1)',
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
          },
        },
        outlinedPrimary: {
          borderWidth: 1.5,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          transition: 'transform 0.3s, box-shadow 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          transition: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        outlinedSuccess: {
          borderColor: '#4cc9f0',
          color: '#0096c7',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: 'rgba(67, 97, 238, 0.08)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        },
        head: {
          backgroundColor: 'rgba(67, 97, 238, 0.03)',
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
          '&:hover': {
            backgroundColor: 'rgba(67, 97, 238, 0.02)',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(180deg, #f8faff 0%, #eff3ff 100%)',
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Mui-selected': {
            background: 'linear-gradient(90deg, #4361ee 0%, #4895ef 100%)',
            boxShadow: '0 4px 12px rgba(67, 97, 238, 0.2)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          transition: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiFade: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          transition: 'none !important',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          transition: 'none !important',
          animation: 'none !important',
        },
      },
    },
  },
});

export default theme; 